import isNumber from 'transmute/isNumber';
import { getCurrencyPriceProperty, isCurrencyPriceProperty } from 'customer-data-objects/lineItem/PropertyNames';
import { getProperty as getObjectProperty, setProperty } from 'customer-data-objects/objects/ObjectAccessors';
import { isPricingModelTiered } from 'customer-data-objects/product/Pricing';
import { getLineItemPropertiesFromProductBatch } from '../api/lineItemsAPI';
import calculateExchangeRatePrice from './calculations/calculateExchangeRatePrice';
import { rethrowError } from './promiseHandler';
import { convertToValue } from './propertyValue';
import { getId, getTemporaryLineItemId } from './subject';

// TODO: Remove this implementation after getObjectProperty supports null
export const getProperty = (...args) => getObjectProperty(...args);
export function convertProductToLineItem(product, currency, multiCurrencies, defaultCurrencyCode, globalTerms = {}) {
  const productId = getId(product);
  const productWithoutCurrencyPrices = Object.assign({}, product, {
    properties: Object.fromEntries(Object.entries(product.properties).filter(([name]) => !isCurrencyPriceProperty(name) && !['createdate', 'hs_status'].includes(name)))
  });

  // Create the initial line item
  const lineItem = Object.assign({}, productWithoutCurrencyPrices, {
    objectId: getTemporaryLineItemId(),
    isNew: true
  });

  // Prepare all properties to set at once
  const propertiesToSet = [{
    name: 'hs_product_id',
    value: String(productId)
  }, {
    name: 'quantity',
    value: '1'
  }, {
    name: 'hs_line_item_currency_code',
    value: currency
  }];
  const pricingModel = getProperty(product, 'hs_pricing_model');
  if (isPricingModelTiered(pricingModel)) {
    // Default `price` to 0 for Tiered Products
    propertiesToSet.push({
      name: 'price',
      value: '0'
    });
  } else {
    if (currency) {
      // Add price and handle unit cost conversion if currency exists
      propertiesToSet.push({
        name: 'price',
        value: getProperty(product, getCurrencyPriceProperty(currency))
      });
    }
  }
  if (currency) {
    // Convert unit cost to deal currency if necessary
    const unitCost = Number(getProperty(lineItem, 'hs_cost_of_goods_sold'));
    if (isNumber(unitCost) && multiCurrencies.length > 0) {
      const unitCostInDealCurrency = calculateExchangeRatePrice(multiCurrencies, defaultCurrencyCode, currency, unitCost);
      propertiesToSet.push({
        name: 'hs_cost_of_goods_sold',
        value: convertToValue(unitCostInDealCurrency)
      });
    }
  }

  // Apply all standard properties
  const lineItemWithProperties = propertiesToSet.reduce((acc, {
    name,
    value
  }) => setProperty(acc, name, value), lineItem);

  // Apply global terms
  return Object.entries(globalTerms).reduce((acc, [key, globalTerm]) => setProperty(acc, key, globalTerm), lineItemWithProperties);
}
export function convertProductsToLineItems(products, currency, multiCurrencies, defaultCurrencyCode, globalTerms) {
  return products.map(product => convertProductToLineItem(product, currency, multiCurrencies, defaultCurrencyCode, globalTerms));
}
export function createLineItemsFromProducts(products, currencyCode, globalTerms) {
  const requests = products.reduce((result, product) => {
    if (product.objectId !== null) {
      result.push({
        productId: product.objectId
      });
    }
    return result;
  }, []);
  return getLineItemPropertiesFromProductBatch({
    requests,
    currencyCode
  }).then(response => {
    const lineItems = response.responses.map(lineItemProperties => {
      const properties = lineItemProperties.properties.reduce((acc, {
        name,
        value
      }) => {
        if (name) {
          acc[name] = {
            name,
            value
          };
        }
        return acc;
      }, {});

      // Apply a quantity of 1 to each line item
      properties.quantity = {
        name: 'quantity',
        value: '1'
      };
      return {
        properties,
        objectId: getTemporaryLineItemId(),
        isNew: true
      };
    });

    // Apply global terms if defined
    if (globalTerms) {
      return lineItems.map(lineItem => {
        return Object.entries(globalTerms).reduce((acc, [key, globalTerm]) => setProperty(acc, key, globalTerm), lineItem);
      });
    }
    return lineItems;
  }).catch(rethrowError);
}